import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ShoppingBag } from "lucide-react";

const DEMO = [
  { label: "Owner", email: "officialcireboncloth@gmail.com", password: "KiyanaOwner2026!" },
  { label: "HR / Admin", email: "hr@kiyana.id", password: "kiyana123" },
  { label: "Supervisor", email: "supervisor@kiyana.id", password: "kiyana123" },
  { label: "Kasir (POS)", email: "kasir@kiyana.id", password: "kiyana123" },
  { label: "Crew Store", email: "crew@kiyana.id", password: "kiyana123" },
  { label: "Gudang", email: "gudang@kiyana.id", password: "kiyana123" },
  { label: "Admin Logistik", email: "logistik@kiyana.id", password: "kiyana123" },
  { label: "Host Live", email: "host@kiyana.id", password: "kiyana123" },
  { label: "Karyawan", email: "karyawan@kiyana.id", password: "kiyana123" },
  { label: "Pelanggan", email: "pelanggan@kiyana.id", password: "kiyana123" },
  { label: "Pengawas Produksi", email: "pengawas@kiyana-test.id", password: "kiyana123" },
  { label: "Cutting Uji (KYN-T002)", email: "cutting@kiyana-test.id", password: "kiyana123" },
  { label: "Mandor Produksi (Desain Baru)", email: "mandor@kiyana-test.id", password: "kiyana123" },
  { label: "Finishing & QC — Maya Kusuma (KYN-0032)", email: "finishing@kiyana-test.id", password: "kiyana123" },
  { label: "Penjahit 1 — Ayu Lestari (JH-001)", email: "penjahit1@kiyana-test.id", password: "kiyana123" },
  { label: "Penjahit 2 — Siti Rahma (JH-002)", email: "penjahit2@kiyana-test.id", password: "kiyana123" },
  { label: "Penjahit 3 — Rina Marlina (JH-003)", email: "penjahit3@kiyana-test.id", password: "kiyana123" },
];

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (em, pw) => {
    setBusy(true);
    try {
      const data = await login(em, pw);
      toast.success(`Selamat datang, ${data.user.name}`);
      nav(data.default_route || "/", { replace: true });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-kiyana-canvas flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-6">
        <Card className="p-8 md:p-10 bg-white border-kiyana-soft shadow-sm rounded-2xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-kiyana-maroon flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-serif-fashion text-2xl text-kiyana-maroon leading-none">KIYANA</div>
              <div className="text-xs tracking-[0.2em] text-kiyana-muted uppercase">by Cirebon Cloth</div>
            </div>
          </div>

          <h1 className="font-serif-fashion text-3xl mb-2">Masuk ke Sistem</h1>
          <p className="text-sm text-kiyana-muted mb-6">Karyawan, admin, dan pelanggan Kiyana.</p>

          <form
            onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@kiyana.id"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy}
              className="w-full h-12 bg-kiyana-maroon hover:bg-kiyana-maroon-dark text-white text-base"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-kiyana-muted">
            Belum punya akun pelanggan? <Link to="/store" className="text-kiyana-maroon font-semibold">Belanja tanpa login</Link>
          </div>
        </Card>

        <Card className="p-8 md:p-10 bg-white border-kiyana-soft rounded-2xl">
          <div className="text-xs tracking-[0.2em] uppercase text-kiyana-muted mb-2">Akun Demo</div>
          <h2 className="font-serif-fashion text-2xl mb-5">Coba semua peran</h2>
          <div className="grid gap-2">
            {DEMO.map((d) => (
              <button
                key={d.email}
                type="button"
                data-testid={`demo-login-${d.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => doLogin(d.email, d.password)}
                disabled={busy}
                className="flex items-center justify-between p-3 rounded-xl border border-kiyana-soft hover:bg-kiyana-soft/40 transition text-left"
              >
                <div>
                  <div className="font-semibold text-sm">{d.label}</div>
                  <div className="text-xs text-kiyana-muted">{d.email}</div>
                </div>
                <div className="text-xs text-kiyana-maroon font-medium">Masuk →</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-kiyana-muted mt-5">Password untuk semua akun demo (kecuali Owner): <b>kiyana123</b></p>
        </Card>
      </div>
    </div>
  );
}
